'use client';

import React from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Button } from '../Button';
import styles from './styles.module.scss';
import { constructClassName } from '../../utils/constructClassName';
import { Container } from '../layouts';

interface Props {
  limit: number;
  page: number;
  totalDocs: number;
}

export const Pagination: React.FC<Props> = ({
  limit,
  page,
  totalDocs,
}) => {
  const searchParams: Record<string, string> = {};
  useSearchParams().forEach((value, key) => {
    searchParams[key] = value;
  });

  const createPageQuery = (newPage: number): Record<string, string | number> => ({
    ...Object.keys(searchParams).reduce((acc, key) => {
      if (key !== 'page') {
        acc[key] = searchParams[key];
      }
      return acc;
    }, {} as Record<string, string>),
    page: newPage,
  });

  return (
    <Container className={styles.container}>
      <Link
        aria-disabled={page === 1}
        className={constructClassName([
          styles.link,
          page <= 1 && styles.disabled,
        ])}
        href={(page > 1) ? {
          query: createPageQuery(page - 1),
        } : {}}
      >
        <Button
          type="tertiary"
          htmlElement="span"
          disabled={page <= 1}
          icon="circleChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>
      </Link>
      <div className={styles.mobilePageNumberContainer}>
        <span>
          Page
          {' '}
          {page}
          {' '}
          of
          {' '}
          {Math.ceil(totalDocs / limit)}
        </span>
      </div>
      <div className={styles.pagePaginationContainer}>
        {page > 1 && (
          <Link
            className={constructClassName([
              styles.pageLink,
              page === 1 && styles.activePageLink,
            ])}
            href={{
              query: createPageQuery(1),
            }}
          >
            1
          </Link>
        )}
        {page > 3 && <span>...</span>}
        {page > 2 && (
          <Link
            className={constructClassName([
              styles.pageLink,
            ])}
            href={{
              query: createPageQuery(page - 1),
            }}
          >
            {page - 1}
          </Link>
        )}
        <Link
          className={constructClassName([
            styles.pageLink,
            styles.activePageLink,
          ])}
          href={{}}
        >
          {page}
        </Link>
        {page < totalDocs / limit && (
          <Link
            className={constructClassName([
              styles.pageLink,
            ])}
            href={{
              query: createPageQuery(page + 1),
            }}
          >
            {page + 1}
          </Link>
        )}
        {page < Math.ceil(totalDocs / limit) - 1 && <span>...</span>}
        {page < Math.ceil(totalDocs / limit - 1) && (
          <Link
            className={constructClassName([
              styles.pageLink,
            ])}
            href={{
              query: createPageQuery(Math.ceil(totalDocs / limit)),
            }}
          >
            {Math.ceil(totalDocs / limit)}
          </Link>
        )}
      </div>
      <Link
        aria-disabled={page * limit >= totalDocs}
        className={constructClassName([
          styles.link,
          page * limit >= totalDocs && styles.disabled,
        ])}
        href={(page * limit <= totalDocs) ? {
          query: createPageQuery(page + 1),
        } : {}}
      >
        <Button
          type="tertiary"
          htmlElement="span"
          disabled={page * limit >= totalDocs}
          icon="circleChevronRight"
        >
          Next
        </Button>
      </Link>
    </Container>
  );
};
